import { apiClient } from "~/services/apiClient";
import type { SearchHighlight, SearchResponse } from "~/types/search";
import { toSearchMeta } from "~/types/search";
import { phonesMatch } from "~/utils/phone";

export type CallHistoryDirection = "incoming" | "outgoing";

export type CallHistoryCustomerSnapshot = {
  medicalConditions?: string[] | null;
  currentMedications?: string[] | null;
  allergies?: string[] | null;
  bloodType?: string | null;
  bloodThinners?: boolean | null;
  emergencyContacts?: unknown[] | null;
  deviceStatus?: unknown[] | null;
};

export type CallHistoryRecord = {
  id: string;
  customerId?: string | null;
  callUuid?: string | null;
  callReference?: string | null;
  callerNumber?: string | null;
  customerName?: string | null;
  operatorName?: string | null;
  operatorNames?: string[] | null;
  telephoneLine?: string | null;
  deviceType?: string | null;
  deviceId?: string | null;
  description?: string | null;
  resolutionReason?: string | null;
  /** What the operator recorded when resolving the call (AC-408). */
  resolutionCategory?: string | null;
  resolutionNotes?: string | null;
  recordingUrl?: string | null;
  recordingFileName?: string | null;
  customerSnapshot?: CallHistoryCustomerSnapshot | null;
  direction: CallHistoryDirection;
  startedAtMs?: number | null;
  endedAtMs: number;
  resolvedAtMs?: number | null;
  durationSeconds: number;
  highlights?: SearchHighlight[];
  fuzzyScore?: number | null;
};

export type HistoricalCallPermissions = {
  canDownloadRecording: boolean;
  canExportCallLog: boolean;
  canViewDownloadDocuments: boolean;
  canCreate: boolean;
  canEdit: boolean;
};

export type HistoricalCallDetail = {
  banner?: string | null;
  readOnly: boolean;
  call: CallHistoryRecord;
  permissions: HistoricalCallPermissions;
};

export type CallRecordingStatus =
  | "queued"
  | "uploading"
  | "ready"
  | "failed"
  | "not_recorded";

export type CallRecordingMetadata = {
  recordingId: string;
  status: CallRecordingStatus;
  filename?: string | null;
  mimeType?: string | null;
  s3Bucket?: string | null;
  s3Key?: string | null;
  sizeBytes?: number | null;
  sha256?: string | null;
  failureReason?: string | null;
  uploadedAt?: string | null;
  lastEventAt?: string | null;
  updatedAt?: string | null;
};

export type CallRecordingStatusResponse = {
  callId: string;
  customerId?: string | null;
  assignedUserId?: string | null;
  recording: CallRecordingMetadata | null;
};

export type CallRecordingUnavailableStatus =
  | CallRecordingStatus
  | "metadata_missing";

export class CallRecordingNotReadyError extends Error {
  status: CallRecordingUnavailableStatus;

  constructor(status: CallRecordingUnavailableStatus) {
    super(`Recording is not ready (status: ${status})`);
    this.name = "CallRecordingNotReadyError";
    this.status = status;
  }
}

type CallHistoryApiUser = {
  id?: string | null;
  name?: string | null;
  surname?: string | null;
  fullName?: string | null;
  sipCredential?: {
    extension?: string | null;
  } | null;
} | null;

type CallHistoryApiCustomer = {
  id?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  name?: string | null;
  fullName?: string | null;
} | null;

type CallHistoryApiDevice = {
  id?: string | null;
  unitId?: string | null;
  deviceId?: string | null;
  type?: string | null;
  deviceType?: string | null;
  typeOtherDescription?: string | null;
} | null;

type CallHistoryApiRecord = {
  id?: string | null;
  asteriskId?: string | null;
  callUuid?: string | null;
  callId?: string | null;
  uniqueId?: string | null;
  trunkNumber?: string | null;
  trunk?: string | null;
  callReference?: string | null;
  callerNumber?: string | null;
  caller?: string | null;
  direction?: string | null;
  status?: string | null;
  startedAt?: string | null;
  endedAt?: string | null;
  resolvedAt?: string | null;
  duration?: number | null;
  durationSeconds?: number | null;
  userId?: string | null;
  user?: CallHistoryApiUser;
  customerId?: string | null;
  customerName?: string | null;
  customer?: CallHistoryApiCustomer;
  deviceType?: string | null;
  deviceId?: string | null;
  unitId?: string | null;
  scaipDeviceId?: string | null;
  device?: CallHistoryApiDevice;
  resolutionReason?: string | null;
  resolutionCategory?: string | null;
  resolutionNotes?: string | null;
  recordingUrl?: string | null;
  recordingFileName?: string | null;
  recording?: {
    accessUrl?: string | null;
    filename?: string | null;
  } | null;
  customerSnapshot?: unknown;
  highlights?: SearchHighlight[];
  fuzzyScore?: number | null;
};

type HistoricalCallApiResponse = {
  data?: {
    banner?: string | null;
    readOnly?: boolean | null;
    call?: CallHistoryApiRecord | null;
    permissions?: Partial<HistoricalCallPermissions> | null;
  } | null;
};

type CallRecordingApiResponse = {
  data?: CallRecordingStatusResponse;
};

type CallRecordingAccessUrlApiResponse = {
  data?: {
    callId?: string | null;
    status?: CallRecordingStatus | null;
    expiresIn?: number | null;
    accessUrl?: string | null;
  };
};

const STORAGE_KEY = "aidcall.call-history.v1";
const UPDATE_EVENT = "aidcall:call-history-updated";
const MAX_RECORDS = 500;

function canUseStorage() {
  return (
    typeof window !== "undefined" &&
    typeof window.localStorage !== "undefined"
  );
}

function toNumber(value: unknown) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

function toOptionalString(value: unknown) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function toOptionalStringArray(value: unknown) {
  if (!Array.isArray(value)) return null;
  const next = value
    .map((item) => toOptionalString(item))
    .filter(Boolean) as string[];
  return next.length > 0 ? next : null;
}

function toDirection(value: unknown): CallHistoryDirection {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  if (
    normalized === "outbound" ||
    normalized === "outgoing" ||
    normalized === "out"
  ) {
    return "outgoing";
  }
  return "incoming";
}

function parseDateMs(value: unknown) {
  if (typeof value !== "string") return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

function normalizeCallStatus(value: unknown) {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  if (!normalized) return null;
  if (normalized === "ended") return "Completed Call";
  if (normalized === "answered") return "Answered Call";
  if (normalized === "ringing") return "Ringing";
  return normalized
    .split(/[\s_-]+/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function toJoinedName(
  firstValue: string | null | undefined,
  secondValue: string | null | undefined
) {
  const first = toOptionalString(firstValue);
  const second = toOptionalString(secondValue);
  if (!first && !second) return null;
  return [first, second].filter(Boolean).join(" ");
}

function resolveApiCustomerName(record: CallHistoryApiRecord) {
  return (
    toOptionalString(record.customerName) ??
    toOptionalString(record.customer?.fullName) ??
    toOptionalString(record.customer?.name) ??
    toJoinedName(record.customer?.firstName, record.customer?.lastName)
  );
}

export function looksLikeInternalRecordId(value: string) {
  // Prisma cuid / cuid2-style ids and UUIDs — not human device unit labels.
  return (
    /^c[a-z0-9]{20,}$/i.test(value) ||
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      value
    )
  );
}

function resolveApiDevice(record: CallHistoryApiRecord) {
  const deviceType =
    toOptionalString(record.deviceType) ??
    toOptionalString(record.device?.deviceType) ??
    toOptionalString(record.device?.type) ??
    toOptionalString(record.device?.typeOtherDescription);

  // Prefer human unit identifiers over Prisma record ids. The call-history API
  // currently sets `deviceId` to `selectedDevice.id` (cuid), with the real unit
  // id on `unitId` / `device.unitId`.
  const candidates = [
    toOptionalString(record.unitId),
    toOptionalString(record.scaipDeviceId),
    toOptionalString(record.device?.unitId),
    toOptionalString(record.device?.deviceId),
    toOptionalString(record.deviceId),
    toOptionalString(record.device?.id),
  ].filter((value): value is string => Boolean(value));

  const deviceId =
    candidates.find((value) => !looksLikeInternalRecordId(value)) ??
    candidates[0] ??
    null;

  return { deviceType, deviceId };
}

function toStringArray(value: unknown) {
  if (!Array.isArray(value)) return null;
  return value
    .map((item) => (typeof item === "string" ? item.trim() : ""))
    .filter(Boolean);
}

function toSnapshot(value: unknown): CallHistoryCustomerSnapshot | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;

  const record = value as Record<string, unknown>;
  const medicalConditions = toStringArray(record.medicalConditions);
  const currentMedications = toStringArray(record.currentMedications);
  const allergies = toStringArray(record.allergies);
  const bloodType =
    typeof record.bloodType === "string" && record.bloodType.trim()
      ? record.bloodType.trim()
      : null;
  const bloodThinners =
    typeof record.bloodThinners === "boolean" ? record.bloodThinners : null;
  const emergencyContacts = Array.isArray(record.emergencyContacts)
    ? record.emergencyContacts
    : null;
  const deviceStatus = Array.isArray(record.deviceStatus)
    ? record.deviceStatus
    : null;

  if (
    medicalConditions === null &&
    currentMedications === null &&
    allergies === null &&
    bloodType === null &&
    bloodThinners === null &&
    emergencyContacts === null &&
    deviceStatus === null
  ) {
    return null;
  }

  return {
    medicalConditions,
    currentMedications,
    allergies,
    bloodType,
    bloodThinners,
    emergencyContacts,
    deviceStatus,
  };
}

function sanitizeRecord(value: unknown): CallHistoryRecord | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;

  const id = typeof record.id === "string" ? record.id : null;
  const endedAtMs = toNumber(record.endedAtMs);
  const durationSecondsRaw = toNumber(record.durationSeconds);
  const directionRaw = record.direction;

  if (!id || endedAtMs === null || durationSecondsRaw === null) return null;

  const direction = toDirection(directionRaw);

  return {
    id,
    customerId:
      typeof record.customerId === "string" ? record.customerId : null,
    callUuid: typeof record.callUuid === "string" ? record.callUuid : null,
    callReference:
      typeof record.callReference === "string" ? record.callReference : null,
    callerNumber:
      typeof record.callerNumber === "string" ? record.callerNumber : null,
    customerName:
      typeof record.customerName === "string" ? record.customerName : null,
    operatorName:
      typeof record.operatorName === "string" ? record.operatorName : null,
    operatorNames: toOptionalStringArray(record.operatorNames),
    telephoneLine: toOptionalString(record.telephoneLine),
    deviceType: toOptionalString(record.deviceType),
    deviceId: toOptionalString(record.deviceId),
    description:
      typeof record.description === "string" ? record.description : null,
    resolutionReason:
      typeof record.resolutionReason === "string"
        ? record.resolutionReason
        : null,
    resolutionCategory: toOptionalString(record.resolutionCategory),
    resolutionNotes: toOptionalString(record.resolutionNotes),
    recordingUrl:
      typeof record.recordingUrl === "string" ? record.recordingUrl : null,
    recordingFileName:
      typeof record.recordingFileName === "string"
        ? record.recordingFileName
        : null,
    customerSnapshot: toSnapshot(record.customerSnapshot),
    direction,
    startedAtMs: toNumber(record.startedAtMs),
    endedAtMs,
    resolvedAtMs: toNumber(record.resolvedAtMs),
    durationSeconds: Math.max(0, Math.floor(durationSecondsRaw)),
  };
}

function safeParse(value: string | null) {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(sanitizeRecord)
      .filter(Boolean) as CallHistoryRecord[];
  } catch {
    return [];
  }
}

function safeWrite(records: CallHistoryRecord[]) {
  if (!canUseStorage()) return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
}

function dedupeAndSort(records: CallHistoryRecord[]) {
  return records
    .filter((item, index, array) => {
      return array.findIndex((candidate) => candidate.id === item.id) === index;
    })
    .sort((a, b) => b.endedAtMs - a.endedAtMs);
}

function mapApiRecord(record: CallHistoryApiRecord): CallHistoryRecord | null {
  const startedAtMs = parseDateMs(record.startedAt);
  const endedAtMs = parseDateMs(record.endedAt) ?? startedAtMs;
  const resolvedAtMs =
    parseDateMs(record.resolvedAt) ?? parseDateMs(record.endedAt);
  if (endedAtMs === null) return null;

  const direction = toDirection(record.direction);
  const durationSecondsRaw =
    toNumber(record.durationSeconds) ?? toNumber(record.duration);
  const durationRaw =
    durationSecondsRaw !== null
      ? durationSecondsRaw
      : startedAtMs !== null
      ? Math.max(0, Math.floor((endedAtMs - startedAtMs) / 1000))
      : 0;

  const operatorName =
    toOptionalString(record.user?.fullName) ??
    toJoinedName(record.user?.name, record.user?.surname) ??
    toOptionalString(record.user?.name) ??
    toOptionalString(record.user?.sipCredential?.extension) ??
    null;
  const statusLabel = normalizeCallStatus(record.status);
  const fallbackDescription =
    direction === "outgoing" ? "Outgoing Call" : "Incoming Call";
  const callUuid =
    toOptionalString(record.asteriskId) ??
    toOptionalString(record.callUuid) ??
    toOptionalString(record.callId) ??
    toOptionalString(record.uniqueId);
  const { deviceType, deviceId } = resolveApiDevice(record);

  return {
    id:
      callUuid ??
      toOptionalString(record.id) ??
      `call-${endedAtMs}`,
    customerId:
      toOptionalString(record.customerId) ??
      toOptionalString(record.customer?.id),
    callUuid,
    callReference:
      toOptionalString(record.callReference) ??
      toOptionalString(record.trunkNumber) ??
      toOptionalString(record.trunk),
    callerNumber:
      toOptionalString(record.callerNumber) ?? toOptionalString(record.caller),
    customerName: resolveApiCustomerName(record),
    operatorName,
    operatorNames: operatorName ? [operatorName] : null,
    telephoneLine:
      toOptionalString(record.trunkNumber) ?? toOptionalString(record.trunk),
    deviceType,
    deviceId,
    description: statusLabel ?? fallbackDescription,
    resolutionReason: toOptionalString(record.resolutionReason),
    resolutionCategory: toOptionalString(record.resolutionCategory),
    resolutionNotes: toOptionalString(record.resolutionNotes),
    recordingUrl:
      toOptionalString(record.recordingUrl) ??
      toOptionalString(record.recording?.accessUrl),
    recordingFileName:
      toOptionalString(record.recordingFileName) ??
      toOptionalString(record.recording?.filename),
    customerSnapshot: toSnapshot(record.customerSnapshot),
    direction,
    startedAtMs,
    endedAtMs,
    resolvedAtMs,
    durationSeconds: Math.max(0, Math.floor(durationRaw)),
    highlights: Array.isArray(record.highlights) ? record.highlights : undefined,
    fuzzyScore:
      typeof record.fuzzyScore === "number" ? record.fuzzyScore : undefined,
  };
}

function normalizeHistoricalPermissions(
  permissions: Partial<HistoricalCallPermissions> | null | undefined
): HistoricalCallPermissions {
  return {
    canDownloadRecording: permissions?.canDownloadRecording === true,
    canExportCallLog: permissions?.canExportCallLog === true,
    canViewDownloadDocuments: permissions?.canViewDownloadDocuments === true,
    canCreate: permissions?.canCreate === true,
    canEdit: permissions?.canEdit === true,
  };
}

function validateAccessUrl(value: string) {
  try {
    const url = new URL(value);
    if (url.protocol === "https:" || url.protocol === "http:") {
      return url.toString();
    }
  } catch {
    // Fall through to a consistent error for malformed or unsafe URLs.
  }

  throw new Error("Recording access response included an invalid access URL.");
}

function normalizeCustomerMatchValue(value: unknown) {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

export function matchesCustomerCallHistoryRecord(
  record: CallHistoryRecord,
  {
    customerId,
    customerName,
    callerNumber,
  }: {
    customerId?: string;
    customerName?: string | null;
    callerNumber?: string | null;
  }
) {
  const normalizedName = normalizeCustomerMatchValue(customerName);
  const hasCriteria = Boolean(customerId || normalizedName || callerNumber);
  if (!hasCriteria) return true;

  const matchesId = Boolean(
    customerId && record.customerId && record.customerId === customerId
  );
  const matchesName =
    normalizedName.length > 0 &&
    normalizeCustomerMatchValue(record.customerName) === normalizedName;
  const matchesPhone =
    Boolean(callerNumber) && phonesMatch(record.callerNumber, callerNumber);

  return matchesId || matchesName || matchesPhone;
}

function emitUpdated() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(UPDATE_EVENT));
}

export function loadCallHistory() {
  if (!canUseStorage()) return [];
  const raw = window.localStorage.getItem(STORAGE_KEY);
  return safeParse(raw).sort((a, b) => b.endedAtMs - a.endedAtMs);
}

export async function loadCallHistoryFromApi(options?: {
  limit?: number;
  page?: number;
  customerId?: string;
  callerNumber?: string;
}) {
  const limit = Math.min(Math.max(options?.limit ?? 200, 1), 200);
  const response = await apiClient.get<{
    data?: CallHistoryApiRecord[];
    meta?: unknown;
  }>("/api/calls", {
    params: {
      limit,
      page: options?.page ?? 1,
      ...(options?.customerId ? { customerId: options.customerId } : {}),
      ...(options?.callerNumber ? { callerNumber: options.callerNumber } : {}),
    },
  });
  const rows = Array.isArray(response.data?.data) ? response.data.data : [];
  return rows
    .map(mapApiRecord)
    .filter(Boolean) as CallHistoryRecord[];
}

export async function loadCustomerCommunicationHistory(options: {
  customerId?: string;
  callerNumber?: string | null;
  limit?: number;
}) {
  const { customerId, callerNumber, limit = 200 } = options;
  const local = loadCallHistory();
  const scope = {
    customerId,
    callerNumber: callerNumber ?? undefined,
  };

  if (!customerId && !callerNumber) {
    return dedupeAndSort(local);
  }

  const localMatches = local.filter((record) =>
    matchesCustomerCallHistoryRecord(record, scope)
  );

  try {
    const api = await loadCallHistoryFromApi({
      limit,
      customerId,
      callerNumber: customerId ? undefined : callerNumber ?? undefined,
    });
    return dedupeAndSort([...api, ...localMatches]);
  } catch {
    return dedupeAndSort(localMatches);
  }
}

export async function searchCallHistoryFromApi(options?: {
  search?: string;
  status?: string;
  userId?: string;
  customerId?: string;
  callerNumber?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
  /** Serialized table filters, see ~/utils/tableFilters. */
  filters?: string;
}): Promise<SearchResponse<CallHistoryRecord>> {
  const params: Record<string, string | number> = {
    page: options?.page ?? 1,
    limit: options?.limit ?? 10,
  };

  if (options?.search?.trim()) params.search = options.search.trim();
  if (options?.status?.trim()) params.status = options.status.trim();
  if (options?.userId?.trim()) params.userId = options.userId.trim();
  if (options?.customerId?.trim()) params.customerId = options.customerId.trim();
  if (options?.callerNumber?.trim()) params.callerNumber = options.callerNumber.trim();
  if (options?.dateFrom?.trim()) params.dateFrom = options.dateFrom.trim();
  if (options?.dateTo?.trim()) params.dateTo = options.dateTo.trim();
  if (options?.filters?.trim()) params.filters = options.filters.trim();

  const response = await apiClient.get<{
    data?: CallHistoryApiRecord[];
    meta?: unknown;
  }>("/api/calls", { params });
  const data = (Array.isArray(response.data?.data) ? response.data.data : [])
    .map(mapApiRecord)
    .filter(Boolean) as CallHistoryRecord[];
  return {
    data,
    meta: toSearchMeta(response.data?.meta, {
      query: options?.search ?? "",
      page: Number(params.page),
      limit: Number(params.limit),
      totalCount: data.length,
      resultCount: data.length,
      totalPages: data.length > 0 ? 1 : 0,
    }),
  };
}

export async function loadHistoricalCallFromApi(
  uniqueId: string
): Promise<HistoricalCallDetail> {
  const response = await apiClient.get<HistoricalCallApiResponse>(
    `/api/calls/historical/${encodeURIComponent(uniqueId)}`
  );
  const payload = response.data?.data;
  const call = payload?.call ? mapApiRecord(payload.call) : null;

  if (!payload || !call) {
    throw new Error("Historical call response did not include a valid call.");
  }

  return {
    banner: payload.banner ?? null,
    readOnly: payload.readOnly !== false,
    call,
    permissions: normalizeHistoricalPermissions(payload.permissions),
  };
}

export async function getCallRecordingStatus(
  callId: string
): Promise<CallRecordingStatusResponse> {
  const response = await apiClient.get<CallRecordingApiResponse>(
    `/api/calls/${encodeURIComponent(callId)}/recording`
  );
  const payload = response.data?.data;

  if (!payload?.callId) {
    throw new Error("Recording status response did not include a call id.");
  }

  return payload;
}

export async function getCallRecordingAccessUrl(callId: string) {
  const response = await apiClient.post<CallRecordingAccessUrlApiResponse>(
    `/api/calls/${encodeURIComponent(callId)}/recording/access-url`,
    {}
  );
  const payload = response.data?.data;

  if (!payload?.accessUrl) {
    throw new Error("Recording access response did not include an access URL.");
  }

  return {
    callId: payload.callId ?? callId,
    status: payload.status ?? "ready",
    expiresIn: payload.expiresIn ?? null,
    accessUrl: validateAccessUrl(payload.accessUrl),
  };
}

export async function getReadyCallRecordingAccessUrl(callId: string) {
  const status = await getCallRecordingStatus(callId);
  const recordingStatus = status.recording?.status ?? "metadata_missing";

  if (recordingStatus !== "ready") {
    throw new CallRecordingNotReadyError(recordingStatus);
  }

  return getCallRecordingAccessUrl(callId);
}

export async function loadUnifiedCallHistory(options?: { limit?: number }) {
  const local = loadCallHistory();
  try {
    const api = await loadCallHistoryFromApi(options);
    return dedupeAndSort([...api, ...local]);
  } catch {
    return dedupeAndSort(local);
  }
}

export function appendCallHistory(record: CallHistoryRecord) {
  if (!canUseStorage()) return;
  const existing = loadCallHistory();
  const next = dedupeAndSort([record, ...existing]).slice(0, MAX_RECORDS);
  safeWrite(next);
  emitUpdated();
}

export function subscribeCallHistory(listener: () => void) {
  if (typeof window === "undefined") return () => {};

  const handleUpdate = () => listener();
  const handleStorage = (event: StorageEvent) => {
    if (event.key !== STORAGE_KEY) return;
    listener();
  };

  window.addEventListener(UPDATE_EVENT, handleUpdate as EventListener);
  window.addEventListener("storage", handleStorage);

  return () => {
    window.removeEventListener(UPDATE_EVENT, handleUpdate as EventListener);
    window.removeEventListener("storage", handleStorage);
  };
}

/**
 * Values a call list filter can offer, taken from every call rather than the page on screen.
 * Pass a customer to limit them to that customer's calls.
 */
export async function getCallFilterOptions(
  field: string,
  scope: { customerId?: string; callerNumber?: string } = {}
): Promise<string[]> {
  const response = await apiClient.get<{ data?: unknown }>(
    "/api/calls/filter-options",
    {
      params: {
        field,
        ...(scope.customerId ? { customerId: scope.customerId } : {}),
        ...(scope.callerNumber ? { callerNumber: scope.callerNumber } : {}),
      },
    }
  );
  const data = response.data?.data;
  return Array.isArray(data)
    ? data.filter((value): value is string => typeof value === "string")
    : [];
}
